import { PixTransactionData } from "@services/models/Prime/GetPixTransactionModel";
import { BrazilPixReceiptData, BrazilPixReceiptParty } from "./types";

const formatDateTimePtBr = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const formatBrazilianDocument = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (digits.length === 14) {
    return digits.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5",
    );
  }
  return value;
};

const partyOrUndefined = (
  party: BrazilPixReceiptParty,
): BrazilPixReceiptParty | undefined => {
  if (!party.name && !party.document && !party.institution) {
    return undefined;
  }
  return party;
};

const ntxPayParty = (
  pix: PixTransactionData,
): BrazilPixReceiptParty | undefined => {
  const party: BrazilPixReceiptParty = {};

  if (pix.accountName) party.name = pix.accountName;
  if (pix.taxId) party.document = formatBrazilianDocument(pix.taxId);
  if (pix.bankName) party.institution = pix.bankName;

  return partyOrUndefined(party);
};

const counterpartParty = (
  pix: PixTransactionData,
): BrazilPixReceiptParty | undefined => {
  const party: BrazilPixReceiptParty = {};

  if (pix.counterpartName) party.name = pix.counterpartName;
  if (pix.counterpartDocument) {
    party.document = formatBrazilianDocument(pix.counterpartDocument);
  }
  if (pix.counterpartAccountBankName) {
    party.institution = pix.counterpartAccountBankName;
  }

  return partyOrUndefined(party);
};

/**
 * PAYMENT: payer = counterpart, recipient = NTX party from API
 * WITHDRAW: recipient = counterpart, payer = NTX party from API
 */
const resolveParties = (
  pix: PixTransactionData,
): Pick<BrazilPixReceiptData, "payer" | "recipient"> => {
  const counterpart = counterpartParty(pix);
  const ntxParty = ntxPayParty(pix);
  const type = (pix.type || "").toUpperCase();

  if (type === "WITHDRAW") {
    return {
      payer: ntxParty,
      recipient: counterpart,
    };
  }

  // PAYMENT (and default)
  return {
    payer: counterpart,
    recipient: ntxParty,
  };
};

export const mapPixTransactionToReceipt = (
  pix: PixTransactionData,
): BrazilPixReceiptData => {
  const { payer, recipient } = resolveParties(pix);
  const receipt: BrazilPixReceiptData = { payer, recipient };

  if (pix.status) receipt.status = pix.status;
  if (typeof pix.originalAmount === "number") {
    receipt.amount = pix.originalAmount;
  }
  if (pix.e2eId) receipt.endToEndId = pix.e2eId;
  if (pix.id) receipt.transactionId = pix.id;
  if (pix.externalId) receipt.externalId = pix.externalId;
  if (pix.processedAt) receipt.settledAt = formatDateTimePtBr(pix.processedAt);

  return receipt;
};
